<template>
    <div class="body-renderer" :class="[`variant-${variant}`, { 'text-left': textAlign === 'left' }]">
        <!-- resource & legal bodies always wrapped every block in a div; their spacing rules rely on it -->
        <component :is="wrapsBlocks ? 'div' : Passthrough" v-for="(block, i) in blocks" :key="block.id ?? i">
            <slot v-if="block.type === 'relatedarticles'" name="relatedarticles" :block="block" />
            <slot v-else-if="block.type === 'caseStudyLeftBlock'" name="caseStudyLeftBlock" :block="block" />
            <slot v-else-if="block.type === 'caseStudyLeftLogo'" name="caseStudyLeftLogo" :block="block" />
            <component :is="resolve(block)" v-else v-bind="blockProps(block, i)" />
        </component>
        <div v-if="$slots.default" class="after-body">
            <slot />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { BodyVariant, EditorJsBlock } from '@model/editor-js/blocks';
import { UnknownBlockTypeError, isBlockType } from '@utils/editor-js';
import { BLOCK_COMPONENTS, SLOT } from './registry.ts';
import type { Component, FunctionalComponent } from 'vue';

defineOptions({
    name: 'ABodyRenderer',
});

const props = withDefaults(defineProps<{
    /** parse stored bodies with `parseBody` from `@utils/editor-js` */
    blocks: EditorJsBlock[];
    variant: BodyVariant;
    textAlign?: 'center' | 'left';
    /** legal pages: anchor every heading by its slugified text for the side navigation */
    setHeadingAnchors?: boolean;
    /** image alt fallback when a block has no caption */
    title?: string;
}>(), {
    textAlign: 'center',
    setHeadingAnchors: false,
    title: '',
});

defineSlots<{
    /** content that belongs to the body's column, after the blocks (e.g. a press release boilerplate) */
    default?: () => unknown;
    relatedarticles?: (scope: { block: Extract<EditorJsBlock, { type: 'relatedarticles' }> }) => unknown;
    caseStudyLeftBlock?: (scope: { block: Extract<EditorJsBlock, { type: 'caseStudyLeftBlock' }> }) => unknown;
    caseStudyLeftLogo?: (scope: { block: Extract<EditorJsBlock, { type: 'caseStudyLeftLogo' }> }) => unknown;
}>();

const wrapsBlocks = computed(() => props.variant === 'resource' || props.variant === 'legal');

const Passthrough: FunctionalComponent = (_, { slots }) => slots.default?.();

function resolve(block: EditorJsBlock): Component {
    if (!isBlockType(block.type)) throw new UnknownBlockTypeError(block.type, block.id, 'blocks');
    const component = BLOCK_COMPONENTS[block.type];
    // a type registered as slot-rendered needs its slot branch in the template above
    if (component === SLOT) throw new Error(`EditorJS block type "${block.type}" has no slot outlet`);
    return component;
}

function blockProps(block: EditorJsBlock, index: number): Record<string, unknown> {
    const common = { id: block.id, data: block.data, tunes: block.tunes, variant: props.variant };
    switch (block.type) {
        case 'header':
            return { ...common, beforeList: props.blocks[index + 1]?.type === 'list', setAnchor: props.setHeadingAnchors };
        case 'image':
        case 'imagegallery':
        case 'quotetool':
        case 'columns':
            return { ...common, title: props.title };
        default:
            return common;
    }
}
</script>

<style lang="pcss" scoped>
.variant-blog {
    overflow-wrap: break-word;
    color: var(--av-fixed-secondary);
    text-align: start;
}

.variant-tru {
    overflow-wrap: break-word;
    color: #000;
    text-align: start;
}

.variant-resource,
.variant-legal {
    max-width: 328px;
    margin-inline-start: auto;
    margin-inline-end: auto;
    text-align: center;

    &.text-left {
        text-align: start;
        margin-inline-start: 0;
        margin-inline-end: 0;
    }

    @media (--viewport-mobile-wide) {
        max-width: 704px;
        text-align: start;
    }

    @media (--viewport-tablet) {
        max-width: initial;
    }

    @media (--viewport-desktop) {
        max-width: 763px;
    }
}

.after-body {
    margin-top: 16px;
}

.variant-event-about {
    overflow-wrap: anywhere;
    color: var(--av-fixed-primary);
    margin-bottom: 38px;
    text-align: start;
}
</style>
